import { AlertTriangle } from 'lucide-react';

const currency = (value: number) => `৳${Number(value || 0).toLocaleString()}`;

/**
 * Details shown in the "Received too high" warning. Keeping them in one object
 * makes the modal easy to reuse from both the create and edit receipt screens.
 */
export interface ReceivedExcessDetails {
  totalAmount: number;
  discountAmount: number;
  netPayable: number;
  received: number;
  excess: number;
}

function Row({
  label,
  value,
  tone = 'default',
  bold,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'danger' | 'muted';
  bold?: boolean;
}) {
  const toneClass =
    tone === 'danger' ? 'text-danger' : tone === 'muted' ? 'text-text-muted' : 'text-text';
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-muted">{label}</span>
      <span className={`${toneClass} ${bold ? 'font-bold' : 'font-medium'}`}>{value}</span>
    </div>
  );
}

/**
 * Warning shown when the amount received at the counter is larger than the
 * amount payable after discount. Accepting it would drive the balance negative,
 * so the caller blocks submission and offers to snap the received value down to
 * the payable amount.
 */
export function ReceivedExcessModal({
  open,
  details,
  onClose,
  onSetToPayable,
}: {
  open: boolean;
  details: ReceivedExcessDetails;
  onClose: () => void;
  onSetToPayable?: () => void;
}) {
  if (!open) return null;

  const { totalAmount, discountAmount, netPayable, received, excess } = details;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md space-y-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-danger/10 flex items-center justify-center shrink-0">
            <AlertTriangle size={22} className="text-danger" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-text">Received amount is too high</h3>
            <p className="text-sm text-text-muted mt-1">
              The received amount cannot exceed the amount payable after discount. Saving this
              would make the balance negative.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-background p-3 space-y-2 text-sm">
          <Row label="Total Amount" value={currency(totalAmount)} />
          <Row label="Discount" value={`-${currency(discountAmount)}`} tone="danger" />
          <div className="h-px bg-border" />
          <Row label="Payable" value={currency(netPayable)} bold />
          <Row label="Received" value={currency(received)} />
          <div className="h-px bg-border" />
          <Row label="Excess" value={currency(excess)} tone="danger" bold />
        </div>

        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text transition-colors"
          >
            Go Back
          </button>
          {onSetToPayable && (
            <button
              type="button"
              onClick={onSetToPayable}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
            >
              Set Received to {currency(netPayable)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
